// src/pages/WrestlerDetails.jsx
import React, { useEffect, useState, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  collection,
  collectionGroup,
  getDoc,
  getDocs,
  doc,
  query,
  where
} from 'firebase/firestore'
import { db } from '../firebase'
import { useAuth } from '../auth/AuthContext'
import { usePremium } from '../premium/PremiumContext'
import { BONUS_LIST } from '../components/BonusList'
import { getStoredBonusDisplay } from '../utils/bonusConfig'
import { getFactionWrestlerIds } from '../utils/factionWrestlers'
import CHAMPIONSHIPS from '../data/championships'
import {
  buildTitleStatsByTitle,
  extractShowIdFromRefPath,
  hasTrackedTitleBonus
} from '../utils/titleStats'
import ScoreChart from '../components/wrestler/ScoreChart'
import PriceChart from '../components/wrestler/PriceChart'
import CreditIcon from '../components/CreditIcon'
import InjuryIcon from '../components/InjuryIcon'
import { PremiumDivisionStats } from '../components/premium/PremiumInfoButton'
import '../styles/WrestlerDetails.css'

const getResultBonusItems = result => {
  const bonuses = Array.isArray(result.bonuses) ? result.bonuses : []
  const bonusPoints = Array.isArray(result.bonusPoints)
    ? result.bonusPoints
    : []

  return bonuses.map((value, index) =>
    getStoredBonusDisplay(
      value,
      baseValue => BONUS_LIST.find(item => item.value === baseValue),
      result.showData,
      bonusPoints[index]
    )
  )
}

const NON_TITLE_AWARD_NAMES = new Map([
  ['WWE Speed Champion', 'Mr. Money In The Bank'],
  ["WWE Women's Speed Champion", 'Ms. Money In The Bank']
])

const getChampionDisplayName = value => {
  const championship = CHAMPIONSHIPS.find(item => item.value === value)
  return championship?.isTitle === false
    ? championship.name
    : NON_TITLE_AWARD_NAMES.get(value) || value
}

const isNonTitleAward = value => {
  const championship = CHAMPIONSHIPS.find(item => item.value === value)
  return championship?.isTitle === false || NON_TITLE_AWARD_NAMES.has(value)
}

function WrestlerDetails () {
  const { id: wrestlerId } = useParams()
  const navigate = useNavigate()
  const { currentUser, authLoading } = useAuth()
  const { isPremium, premiumLoading } = usePremium()
  const [wrestler, setWrestler] = useState(null)
  const [titleStats, setTitleStats] = useState({})
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(true)
  const [userFactionsWithWrestler, setUserFactionsWithWrestler] = useState([])
  const rosterIcons = {
    Raw: 'https://www.wwe.com/f/styles/wwe_show_logo_network_l/public/all/2024/12/RAW_Primary_Logo_For_Any_Background_(1)--de3612b9543981440a24489e0e568d9b.png',
    SmackDown:
      'https://www.wwe.com/f/styles/wwe_show_logo_network_l/public/all/2024/09/Smackdown_LOGO--c8e0b09904cb1356d309a8ea2acb0422.png',
    NXT: 'https://www.wwe.com/f/styles/wwe_show_logo_network_l/public/all/2024/10/NXTonCW--0f06792893241464479338f55596a5bf.png'
  }
  const [showAll, setShowAll] = useState(false)
  const [showAllSeasonScores, setShowAllSeasonScores] = useState(false)
  const [team, setTeam] = useState(null)
  const averageScore = useMemo(() => {
    if (results.length === 0) return 0
    const total = results.reduce((sum, result) => sum + (result.total || 0), 0)
    return total / results.length
  }, [results])

  const sortedResults = [...results].sort((a, b) => {
    if (!a.date && !b.date) return 0
    if (!a.date) return 1
    if (!b.date) return -1
    return b.date.getTime() - a.date.getTime()
  })

  const seasonScores = useMemo(() => {
    if (!Array.isArray(wrestler?.scores)) return []

    return wrestler.scores
      .map((score, index) => ({
        seasonLabel: `Season ${index}`,
        score,
        index
      }))
      .reverse()
  }, [wrestler])

  const visibleSeasonScores = showAllSeasonScores
    ? seasonScores
    : seasonScores.slice(0, 3)

  const bonusStats = useMemo(
    () =>
      Object.entries(
        results.reduce((stats, result) => {
          getResultBonusItems(result).forEach(bonus => {
            const points = Number(bonus.points) || 0

            if (!stats[bonus.value]) {
              stats[bonus.value] = {
                baseValue: bonus.baseValue,
                label: bonus.label,
                points,
                totalPoints: 0,
                isExtVariant: bonus.isExtVariant,
                count: 0
              }
            }

            stats[bonus.value].totalPoints += points
            stats[bonus.value].count += 1
          })
          return stats
        }, {})
      ).sort(
        (a, b) =>
          b[1].count - a[1].count || a[1].label.localeCompare(b[1].label)
      ),
    [results]
  )

  const rosterIcon = wrestler ? rosterIcons[wrestler.brand] || null : null

  useEffect(() => {
    if (premiumLoading) return

    async function fetchData () {
      try {
        setLoading(true)
        let fetchedChampionTypes = []

        // Fetch wrestler info
        const wRef = doc(db, 'wrestlers', wrestlerId)
        const wSnap = await getDoc(wRef)

        // Fetch team info
        const teamsSnapshot = await getDocs(collection(db, 'teams'))
        const team = teamsSnapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .find(t => t.members?.includes(wrestlerId))

        if (team) {
          const teamMembers = await Promise.all(
            team.members
              .filter(id => id !== wrestlerId)
              .map(async id => {
                const memberSnap = await getDoc(doc(db, 'wrestlers', id))
                if (!memberSnap.exists()) return null

                const memberData = memberSnap.data()
                const {
                  premiumComment,
                  position,
                  storyline,
                  ...standardMemberData
                } = memberData

                return {
                  id,
                  ...standardMemberData,
                  ...(isPremium
                    ? { premiumComment, position }
                    : {})
                }
              })
          )
          setTeam({ ...team, members: teamMembers.filter(Boolean) })
        }

        if (wSnap.exists()) {
          const data = wSnap.data()
          const {
            premiumComment,
            position,
            storyline,
            ...standardData
          } = data
          // Mantieni compatibilità con i vecchi dati
          const championTypes = Array.isArray(data.championTypes)
            ? data.championTypes.filter(Boolean)
            : []
          const championImages = data.championImages
          fetchedChampionTypes = championTypes.filter(
            championType => !isNonTitleAward(championType)
          )

          // Prendi l'array prices direttamente così com'è
          const prices = data.prices || []

          // DEBUG: Log per vedere i dati grezzi
          console.log('Dati grezzi da Firestore:', {
            prices: prices,
            pricesCount: prices.length,
            firstPrice: prices[0],
            price: data.price
          })

          setWrestler({
            id: wrestlerId,
            name: standardData.name || standardData.nome || 'Sconosciuto',
            imageUrl: standardData.imageUrl || '',
            score: standardData.score || 0,
            scores: Array.isArray(standardData.scores)
              ? standardData.scores
              : Array.isArray(standardData.pastScores)
              ? standardData.pastScores
              : [],
            brand: standardData.brand || '',
            price: standardData.price || 0,
            alignment: standardData.alignment,
            championTypes: championTypes,
            championImages: championImages,
            isInjured: standardData.isInjured || false,
            active: standardData.active !== undefined ? standardData.active : true,
            ...(isPremium
              ? {
                  premiumComment: premiumComment || '',
                  position: position ?? 0
                }
              : {}),
            prices: prices // Mantieni l'array originale da Firestore
          })
        } else {
          setTitleStats({})
        }

        const pSnap = await getDocs(collectionGroup(db, 'participations'))
        const filtered = pSnap.docs
          .map(d => ({
            id: d.id,
            ...d.data(),
            refPath: d.ref.path
          }))
          .filter(participation => participation.wrestlerId === wrestlerId)

        const showIds = [
          ...new Set(
            filtered
              .map(participation =>
                extractShowIdFromRefPath(participation.refPath)
              )
              .filter(Boolean)
          )
        ]
        const titleTrackedShowIds = new Set(
          filtered
            .filter(hasTrackedTitleBonus)
            .map(participation =>
              extractShowIdFromRefPath(participation.refPath)
            )
            .filter(Boolean)
        )

        const showEntries = await Promise.all(
          showIds.map(async showId => {
            const [showSnap, matchesSnap] = await Promise.all([
              getDoc(doc(db, 'shows', showId)),
              titleTrackedShowIds.has(showId)
                ? getDocs(collection(db, 'shows', showId, 'matches'))
                : Promise.resolve(null)
            ])

            const showData = showSnap.exists() ? showSnap.data() : {}
            const showDate =
              typeof showData?.data?.toDate === 'function'
                ? showData.data.toDate()
                : showData?.data instanceof Date
                ? showData.data
                : null

            return {
              showId,
              showData,
              showDate,
              matches: matchesSnap
                ? matchesSnap.docs.map(matchDoc => ({
                    id: matchDoc.id,
                    ...matchDoc.data()
                  }))
                : []
            }
          })
        )

        const showEntriesById = new Map(
          showEntries.map(entry => [entry.showId, entry])
        )

        const showsData = filtered.map(participation => {
          const showId = extractShowIdFromRefPath(participation.refPath)
          const showEntry = showEntriesById.get(showId) || {}
          const showData = showEntry.showData || {}

          return {
            showId,
            showName: showData.nome || showId,
            showLogo: showData.logo,
            date: showEntry.showDate || null,
            bonuses: participation.bonuses || [],
            bonusPoints: Array.isArray(participation.bonusPoints)
              ? participation.bonusPoints
              : [],
            showData: {
              type: showData.type,
              isExtra: Boolean(showData.isExtra)
            },
            total: participation.totalPoints || 0
          }
        })

        setResults(showsData)
        setTitleStats(
          buildTitleStatsByTitle({
            wrestlerId,
            titleValues: fetchedChampionTypes,
            participations: filtered,
            matchesByShowId: new Map(
              showEntries.map(entry => [entry.showId, entry.matches])
            ),
            showDatesByShowId: new Map(
              showEntries.map(entry => [entry.showId, entry.showDate])
            )
          })
        )
      } catch (error) {
        console.error('Error fetching wrestler details:', error)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [isPremium, premiumLoading, wrestlerId])

  useEffect(() => {
    let isMounted = true

    const fetchUserFactionsWithWrestler = async () => {
      if (authLoading) return

      if (!currentUser?.uid || !wrestlerId) {
        if (isMounted) setUserFactionsWithWrestler([])
        return
      }

      try {
        const userSnap = await getDoc(doc(db, 'users', currentUser.uid))
        const userData = userSnap.exists() ? userSnap.data() : null
        const preferredFactionIds = [
          ...(Array.isArray(userData?.factionIds) ? userData.factionIds : []),
          userData?.factionId,
          userData?.favoriteFactionId
        ]
          .filter(Boolean)
          .filter(
            (factionId, index, factionIds) =>
              factionIds.indexOf(factionId) === index
          )

        let factions = []

        if (preferredFactionIds.length > 0) {
          const factionDocs = await Promise.all(
            preferredFactionIds.map(factionId =>
              getDoc(doc(db, 'factions', factionId))
            )
          )

          factions = factionDocs
            .filter(factionDoc => factionDoc.exists())
            .map(factionDoc => ({
              id: factionDoc.id,
              ...factionDoc.data()
            }))
        } else {
          const factionsSnap = await getDocs(
            query(
              collection(db, 'factions'),
              where('ownerUid', '==', currentUser.uid)
            )
          )

          factions = factionsSnap.docs.map(factionDoc => ({
            id: factionDoc.id,
            ...factionDoc.data()
          }))
        }

        const rosterIds = [
          ...new Set(factions.map(faction => faction.rosterId).filter(Boolean))
        ]
        const rosterDocs = await Promise.all(
          rosterIds.map(rosterId => getDoc(doc(db, 'rosters', rosterId)))
        )
        const rosterNamesById = rosterDocs.reduce((acc, rosterDoc) => {
          if (!rosterDoc.exists()) return acc

          acc[rosterDoc.id] = rosterDoc.data()?.name || 'Roster'
          return acc
        }, {})

        const matchingFactions = factions
          .filter(faction => getFactionWrestlerIds(faction).includes(wrestlerId))
          .map(faction => ({
            ...faction,
            rosterLabel:
              rosterNamesById[faction.rosterId] ||
              faction.rosterName ||
              faction.roster ||
              ''
          }))
          .sort((a, b) =>
            (a.name || 'Fazione').localeCompare(b.name || 'Fazione')
          )

        if (isMounted) {
          setUserFactionsWithWrestler(matchingFactions)
        }
      } catch (error) {
        console.error(
          "Errore nel caricamento delle fazioni dell'utente:",
          error
        )

        if (isMounted) setUserFactionsWithWrestler([])
      }
    }

    fetchUserFactionsWithWrestler()

    return () => {
      isMounted = false
    }
  }, [authLoading, currentUser?.uid, wrestlerId])

  const navigateToShow = (showId, e) => {
    if (e.target.closest('.badge')) return
    navigate(`/shows/${showId}`)
  }

  // Funzione per ottenere la variazione del prezzo
  const getPriceChangeInfo = () => {
    if (!wrestler || !wrestler.prices || wrestler.prices.length < 2) {
      return null
    }

    const prices = wrestler.prices
    const currentPrice = prices[prices.length - 1].value
    const previousPrice = prices[prices.length - 2].value
    const change = currentPrice - previousPrice
    const changePercentage = ((change / previousPrice) * 100).toFixed(1)

    return {
      change,
      changePercentage,
      direction: change > 0 ? 'up' : change < 0 ? 'down' : 'same',
      previousPrice,
      currentPrice
    }
  }

  const priceChange = getPriceChangeInfo()

  // Funzione per calcolare i giorni campione
  const calculateTitleDays = (wrestlerId, titleType) => {
    if (!wrestlerId || !titleType) return 0
    return titleStats[titleType]?.daysAsChampion || 0
  }

  if (loading) {
    return (
      <div className='wrestler-details-page container py-4 text-center'>
        <div
          className='spinner-border text-brand-primary-ultralight'
          role='status'
        >
          <span className='visually-hidden'>Caricamento...</span>
        </div>
      </div>
    )
  }

  if (!wrestler) {
    return (
      <div className='wrestler-details-page container py-4 text-center'>
        <div className='bonus-details-state'>
          <p className='mb-0'>Wrestler non trovato.</p>
        </div>
      </div>
    )
  }

  return (
    <main className='wrestler-details-page'>
      <div className='container wrestler-details-shell py-3 py-md-4'>
        <div className='row g-3 g-lg-4'>
          {/* Left Column - Profile Card */}
          <div className='col-12 col-lg-4'>
            <div className='card border-0 shadow-sm wrestler-detail-card'>
              <div
                className='position-relative d-flex justify-content-center align-items-center wrestler-detail-hero rounded-top'
                style={{ minHeight: '200px', overflow: 'hidden' }}
              >
                {/* Champion Image in bottom-left corner */}
                {wrestler.championImages && wrestler.championImages.length > 0 && (
                  <div className='champion-images-container'>
                    {wrestler.championImages
                      .map(
                        (img, index) =>
                          img && (
                            <div key={index} className='champion-image-item'>
                              <img
                                src={img}
                                alt={
                                  wrestler.championTypes
                                    ? getChampionDisplayName(
                                        wrestler.championTypes[index]
                                      )
                                    : 'Champion'
                                }
                              />
                            </div>
                          )
                      )
                      .filter(Boolean)}
                  </div>
                )}

                {wrestler.imageUrl ? (
                  <img
                    src={wrestler.imageUrl}
                    alt={wrestler.name}
                    className='wrestler-image-container'
                  />
                ) : (
                  <div className='wrestler-placeholder'>
                    <i className='bi bi-person'></i>
                  </div>
                )}

                {rosterIcon && (
                  <img
                    src={rosterIcon}
                    alt={wrestler.brand}
                    className='roster-logo'
                  />
                )}

                {wrestler.alignment && (
                  <span className='alignment-badge'>
                    {wrestler.alignment === 'face' && (
                      <i
                        className='bi bi-emoji-smile-fill alignment-icon face'
                        title='Face'
                      ></i>
                    )}
                    {wrestler.alignment === 'heel' && (
                      <i
                        className='bi bi-emoji-angry-fill alignment-icon heel'
                        title='Heel'
                      ></i>
                    )}
                    {wrestler.alignment.charAt(0).toUpperCase() +
                      wrestler.alignment.slice(1)}
                  </span>
                )}
              </div>

              <div className='card-body d-flex flex-column gap-3'>
                {/* Nome e punteggio */}
                <div className='wrestler-detail-summary d-flex justify-content-between align-items-start'>
                  <div className='wrestler-detail-title-block d-flex flex-column gap-2 me-3'>
                    <h1 className='wrestler-detail-title mb-0 text-white'>
                      {wrestler.name}
                    </h1>

                    {/* Champion Badges */}
                    {wrestler.championTypes &&
                      wrestler.championTypes.length > 0 && (
                        <div className='d-flex flex-wrap gap-2'>
                          {wrestler.championTypes.map((type, index) => {
                            if (!type) return null
                            const displayName = getChampionDisplayName(type)
                            const isAward = isNonTitleAward(type)
                            const days = isAward
                              ? 0
                              : calculateTitleDays(wrestler.id, type)
                            const daysLabel = days > 0 ? days : '--'
                            return (
                              <span
                                key={index}
                                className='badge bg-warning bg-opacity-25 text-warning border border-warning px-3 py-2 rounded-pill'
                              >
                                {displayName}
                                {!isAward && (
                                  <small className='ms-1'>
                                    &middot; {daysLabel} gg
                                  </small>
                                )}
                              </span>
                            )
                          })}
                        </div>
                      )}

                    {/* Status Badges */}
                    <div className='d-flex flex-wrap gap-2'>
                      {wrestler.isInjured && (
                        <span className='badge wrestlers-brand-badge-raw text-danger border border-danger px-3 py-2 rounded-pill'>
                          <InjuryIcon
                            size={18}
                            iconSize='0.85rem'
                            className='me-1'
                          />
                          Infortunato
                        </span>
                      )}
                      {!wrestler.active && (
                        <span className='badge bg-secondary bg-opacity-25 text-brand-muted border border-secondary px-3 py-2 rounded-pill'>
                          <i className='bi bi-ban me-1'></i>
                          Wrestler non attivo
                        </span>
                      )}
                    </div>
                  </div>
                  <div
                    className={`wrestler-score-badge text-center bg-${
                      wrestler.brand?.toLowerCase() === 'raw'
                        ? 'danger'
                        : wrestler.brand?.toLowerCase() === 'smackdown'
                        ? 'primary'
                        : 'secondary'
                    } bg-opacity-25 rounded p-2`}
                  >
                    <span className='fs-2 fw-bold text-white'>
                      {wrestler.score}
                    </span>
                  </div>
                </div>

                {/* Record Stats */}
                {(() => {
                  const stats = results.reduce(
                    (acc, result) => {
                      const bonuses = Array.isArray(result.bonuses)
                        ? result.bonuses
                        : []
                      bonuses.forEach(b => {
                        if (b === 'match_sett' || b === 'match_ple')
                          acc.losses += 1
                        if (b === 'vince_sett' || b === 'vince_ple') {
                          acc.wins += 1
                          acc.losses -= 1
                        }
                      })
                      return acc
                    },
                    { wins: 0, losses: 0 }
                  )

                  const total = stats.wins + stats.losses
                  const winPercentage =
                    total > 0 ? ((stats.wins / total) * 100).toFixed(1) : 0

                  return (
                    <div className='bg-dark bg-opacity-25 p-3 rounded-3'>
                      <div className='d-flex justify-content-around text-center mb-3'>
                        <div>
                          <span className='display-6 fw-bold text-success'>
                            {stats.wins}
                          </span>
                          <div className='small text-white-50'>VITTORIE</div>
                        </div>
                        <div className='vr text-light'></div>
                        <div>
                          <span className='display-6 fw-bold text-danger'>
                            {stats.losses}
                          </span>
                          <div className='small text-white-50'>SCONFITTE</div>
                        </div>
                      </div>

                      {total > 0 && (
                        <div>
                          <div className='d-flex justify-content-between small mb-1'>
                            <span className='text-white-50'>
                              Perc. vittorie
                            </span>
                            <span className='fw-bold text-brand-primary-ultralight'>
                              {winPercentage}%
                            </span>
                          </div>
                          <div className='progress' style={{ height: '6px' }}>
                            <div
                              className='progress-bar bg-success'
                              style={{ width: `${winPercentage}%` }}
                            ></div>
                          </div>
                          <div className='d-flex justify-content-between small mt-2'>
                            <span className='text-white-50'>Match totali</span>
                            <span className='fw-bold text-brand-primary-ultralight'>
                              {total}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })()}

                {/* Quotazione */}
                <div className='bg-dark bg-opacity-25 p-3 rounded-3'>
                  <div className='d-flex justify-content-between align-items-center'>
                    <span className='text-white'>Quotazione</span>
                    <div className='text-end'>
                      <span className='fs-3 fw-bold text-money'>
                        {wrestler.price} <CreditIcon size={26} />
                      </span>
                      {priceChange && priceChange.direction !== 'same' && (
                        <div
                          className={`small ${
                            priceChange.direction === 'up'
                              ? 'text-success'
                              : 'text-danger'
                          }`}
                        >
                          {priceChange.direction === 'up' ? (
                            <>
                              <i className='bi bi-caret-up-fill'></i> +
                              {priceChange.change}
                            </>
                          ) : (
                            <>
                              <i className='bi bi-caret-down-fill'></i>{' '}
                              {priceChange.change}
                            </>
                          )}
                          <span className='ms-1'>
                            ({priceChange.changePercentage}%)
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {isPremium && (
                  <div className='premium-wrestler-detail-stack'>
                    {wrestler.premiumComment && (
                      <div className='premium-wrestler-detail-comment'>
                        <p>{wrestler.premiumComment}</p>
                      </div>
                    )}
                    <PremiumDivisionStats wrestler={wrestler} />
                  </div>
                )}

                {/* Punteggi stagioni passate */}
                {seasonScores.length > 0 && (
                  <div className='record-card mt-3 p-3 wrestler-detail-subcard rounded-3'>
                    <div className='d-flex justify-content-between align-items-center mb-3'>
                      <span className='text-brand-muted small'>
                        Storico stagioni
                      </span>
                      <span className='text-muted-brand small'>
                        {seasonScores.length} stagion
                        {seasonScores.length === 1 ? 'e' : 'i'}
                      </span>
                    </div>

                    <div className='d-flex flex-column gap-2'>
                      {visibleSeasonScores.map(
                        ({ seasonLabel, score, index }) => (
                          <div
                            key={index}
                            className='d-flex justify-content-between align-items-center px-3 py-2 rounded-3'
                            style={{
                              background: 'rgba(38, 56, 72, 0.55)',
                              border: '1px solid rgba(146, 201, 252, 0.14)'
                            }}
                          >
                            <span className='small text-light fw-semibold'>
                              {seasonLabel}
                            </span>
                            <span className='text-brand-secondary-light fw-bold'>
                              {score}
                            </span>
                          </div>
                        )
                      )}
                    </div>

                    {seasonScores.length > 3 && (
                      <div className='mt-3 text-center'>
                        <button
                          type='button'
                          className='btn btn-sm btn-outline-brand-secondary'
                          onClick={() => setShowAllSeasonScores(prev => !prev)}
                        >
                          {showAllSeasonScores
                            ? 'Mostra meno'
                            : `Mostra tutte (${seasonScores.length})`}
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {currentUser && userFactionsWithWrestler.length > 0 && (
                  <div className='record-card mt-3 p-3 wrestler-detail-subcard rounded-3'>
                    <div className='d-flex justify-content-between align-items-center mb-3'>
                      <span className='text-brand-muted small'>
                        Questo wrestler è presente in {userFactionsWithWrestler.length} tu{userFactionsWithWrestler.length === 1 ? 'a' : 'e'} fazion{userFactionsWithWrestler.length === 1 ? 'e' : 'i'}
                      </span>
                    </div>

                    <div className='d-flex flex-column gap-2'>
                      {userFactionsWithWrestler.map(faction => (
                        <button
                          key={faction.id}
                          type='button'
                          className='wrestler-detail-user-faction-row'
                          onClick={() => navigate(`/factions/${faction.id}`)}
                        >
                          <span className='wrestler-detail-user-faction-row__content'>
                            <span className='wrestler-detail-user-faction-row__name small text-light fw-semibold'>
                              {faction.name || 'Fazione senza nome'}
                            </span>
                          </span>

                          <span className='wrestler-detail-user-faction-row__meta'>
                            {faction.rosterLabel && (
                              <span className='wrestler-detail-user-faction-row__roster text-brand-secondary-light fw-bold'>
                                {faction.rosterLabel}
                              </span>
                            )}
                            <i className='bi bi-chevron-right text-brand-muted'></i>
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Team Section */}
                {team && (
                  <div className='team-section'>
                    <div className='team-name'>{team.name}</div>
                    {team.members.length > 0 && (
                      <div className='team-members'>
                        {team.members.map(member => (
                          <div
                            key={member.id}
                            className='team-member-avatar'
                            title={member.name}
                            onClick={() => navigate(`/wrestlers/${member.id}`)}
                          >
                            <img src={member.imageUrl} alt={member.name} />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Results and Stats */}
          <div className='col-12 col-lg-8'>
            {/* Results Card */}
            <div className='card border-0 shadow-sm mb-4 wrestler-detail-card'>
              <div className='results-card-header'>
                <i className='bi bi-trophy-fill'></i>
                <h3>Partecipazioni e Risultati</h3>
              </div>

              <div className='card-body p-0 wrestler-detail-card-body'>
                {results.length === 0 ? (
                  <div className='empty-state-container wrestler-detail-empty-state'>
                    <i className='bi bi-inbox empty-state-icon'></i>
                    <p className='empty-state-text'>
                      Nessun risultato registrato
                    </p>
                  </div>
                ) : (
                  <>
                    <div
                      className='results-table-container'
                      style={{ maxHeight: showAll ? undefined : '400px' }}
                    >
                      <table className='results-table'>
                        <thead>
                          <tr>
                            <th>Evento</th>
                            <th>Data</th>
                            <th className='text-end'>Punti</th>
                            <th>Bonus/Malus</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedResults
                            .slice(0, showAll ? sortedResults.length : 5)
                            .map((r, i) => (
                              <tr
                                key={i}
                                onClick={e => navigateToShow(r.showId, e)}
                              >
                                <td
                                  className='results-event-cell'
                                  data-label='Evento'
                                >
                                  <div className='results-event'>
                                    {r.showLogo ? (
                                      <img
                                        src={r.showLogo}
                                        alt={r.showName}
                                        className='show-logo results-event-logo'
                                      />
                                    ) : (
                                      <span className='results-event-logo results-event-logo--empty'>
                                        <i className='bi bi-calendar-event' />
                                      </span>
                                    )}
                                    <span className='results-event-name'>
                                      {r.showName}
                                    </span>
                                  </div>
                                </td>
                                <td
                                  className='results-date-cell'
                                  data-label='Data'
                                >
                                  {r.date
                                    ? r.date.toLocaleDateString('it-IT')
                                    : '-'}
                                </td>
                                <td className='points' data-label='Punti'>
                                  {r.total}
                                </td>
                                <td data-label='Bonus/Malus'>
                                  <div className='bonus-badges'>
                                    {getResultBonusItems(r).map(
                                      (bonus, idx) => {
                                        let badgeClass = 'neutral'
                                        if (bonus.points > 0) {
                                          badgeClass = 'positive'
                                        } else if (bonus.points < 0) {
                                          badgeClass = 'negative'
                                        }

                                        return (
                                          <span
                                            key={idx}
                                            className={`bonus-badge ${badgeClass} ${
                                              bonus.isExtVariant
                                                ? 'bonus-badge--ext'
                                                : ''
                                            }`}
                                            title={
                                              bonus.isExtVariant
                                                ? `${
                                                    bonus.desc || bonus.label
                                                  } (Extra)`
                                                : bonus.desc
                                            }
                                            onClick={e => {
                                              e.stopPropagation()
                                              navigate(
                                                `/bonus/${encodeURIComponent(
                                                  bonus.baseValue
                                                )}`
                                              )
                                            }}
                                          >
                                            <span className='bonus-badge__label'>
                                              {bonus.label}
                                            </span>
                                            <span className='bonus-badge__points'>
                                              {bonus.points > 0 ? '+' : ''}
                                              {bonus.points}
                                            </span>
                                          </span>
                                        )
                                      }
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>

                    {results.length > 5 && (
                      <div className='text-center py-3 border-top wrestler-detail-footer'>
                        <button
                          className='show-all-button'
                          onClick={() => setShowAll(!showAll)}
                        >
                          {showAll ? (
                            <>
                              <i className='bi bi-chevron-up me-1'></i> Mostra
                              meno
                            </>
                          ) : (
                            <>
                              <i className='bi bi-chevron-down me-1'></i> Mostra
                              tutti ({results.length})
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Bonus Statistics Card */}
            <div className='card border-0 shadow-sm mb-4 wrestler-detail-card'>
              <div className='stats-card-header'>
                <i className='bi bi-pie-chart-fill'></i>
                <h4>Statistiche Bonus/Malus</h4>
              </div>

              <div className='card-body wrestler-detail-card-body'>
                {bonusStats.length === 0 ? (
                  <div className='text-center py-4 wrestler-detail-chart-empty rounded'>
                    <i className='bi bi-pie-chart fs-4 text-muted-brand'></i>
                    <p className='mt-2 mb-0 small'>
                      Nessun bonus/malus registrato
                    </p>
                  </div>
                ) : (
                  <div className='bonus-stats-list'>
                    {bonusStats.map(([value, data]) => {
                      const toneClass =
                        data.totalPoints > 0
                          ? 'bonus-stat-row--positive'
                          : data.totalPoints < 0
                          ? 'bonus-stat-row--negative'
                          : 'bonus-stat-row--neutral'

                      return (
                        <button
                          key={value}
                          type='button'
                          className={`bonus-stat-row ${toneClass}`}
                          title='Clicca per vedere i wrestler'
                          onClick={() =>
                            navigate(
                              `/bonus/${encodeURIComponent(
                                data.baseValue || value
                              )}`
                            )
                          }
                        >
                          <span className='bonus-stat-row__content'>
                            <span className='bonus-stat-row__label'>
                              {data.label}
                            </span>
                          </span>
                          <span className='fw-bold fs-5 d-flex text-end'>{data.count}</span>
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Charts Section - 2 colonne su desktop */}
            <div className='row g-4'>
              {/* Score Chart */}
              <div className='col-12 col-md-6'>
                <div className='card border-0 shadow-sm h-100 wrestler-detail-card'>
                  <div className='wrestlercard-header border-bottom px-1 py-3'>
                    <h4 className='h5 mb-0 fw-bold'>
                      <i className='bi bi-graph-up-arrow text-warning me-2'></i>
                      Andamento Punteggio
                    </h4>
                  </div>
                  <div className='card-body p-2 wrestler-detail-card-body'>
                    {results.length >= 2 ? (
                      <ScoreChart
                        data={sortedResults.slice(0, 10).map(r => ({
                          date: r.date,
                          total: r.total
                        }))}
                        average={averageScore} // Passa la media calcolata
                        compact={true}
                        height={150}
                      />
                    ) : (
                      <div className='text-center py-4 wrestler-detail-chart-empty rounded'>
                        <i className='bi bi-bar-chart-line fs-4 text-muted-brand'></i>
                        <p className='mt-2 mb-0 small'>
                          Dati insufficienti per visualizzare il grafico
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Price Chart */}
              <div className='col-12 col-md-6'>
                <div className='card border-0 shadow-sm h-100 wrestler-detail-card'>
                  <div className='wrestlercard-header border-bottom px-1 py-3'>
                    <h4 className='h5 mb-0 fw-bold'>
                      <CreditIcon
                        className='me-1  text-success mb-2'
                        size={25}
                      />
                      Andamento Quotazioni
                    </h4>
                  </div>
                  <div className='card-body p-2 wrestler-detail-card-body'>
                    {wrestler.prices && wrestler.prices.length >= 2 ? (
                      <>
                        <PriceChart
                          data={wrestler.prices}
                          compact={true}
                          height={150}
                        />
                      </>
                    ) : (
                      <div className='text-center py-4 wrestler-detail-chart-empty rounded'>
                        <CreditIcon
                          className='me-1 fs-4 text-muted-brand mb-2'
                          size={25}
                        />
                        <p className='mt-2 mb-0 small'>
                          Nessuna cronologia quotazioni disponibile
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  )
}

export default WrestlerDetails
